import { Link } from 'react-router-dom';
import { S } from '@/shared/constants/styles';
import { useHouseholdData } from '@/app/providers/HouseholdDataProvider';
import { DEFAULT_DATA } from '@/shared/constants/config';
import { migrateSavings, deriveGoal } from '@/features/savings/utils/savingsMigration';

export default function SavingsGoalsSummary({ currency = '$', theme: t }) {
  const { data } = useHouseholdData();
  const safe = data || DEFAULT_DATA;
  const savings = migrateSavings(safe.savings);
  const goals = Object.values(savings.goals || {}).sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)).map(deriveGoal);

  const cardBg = t?.cardBg || 'white';
  const cardBorder = t?.cardBorder || '#eee';
  const textColor = t?.text || '#1e293b';
  const textMuted = t?.textMuted || '#94a3b8';
  const inputBg = t?.inputBg || '#f8fafc';

  if (goals.length === 0) {
    return (
      <div style={{ ...S.card, background: cardBg, borderColor: cardBorder, padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: textColor }}>Savings Goals</div>
          <Link to="/savings" style={{ fontSize: 12, color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>Set up goals</Link>
        </div>
        <div style={{ color: textMuted, fontSize: 13, textAlign: 'center', padding: '14px 0' }}>No goals yet</div>
      </div>
    );
  }

  const totalSaved = goals.reduce((s, g) => s + (g.saved || 0), 0);
  const totalTarget = goals.reduce((s, g) => s + (g.target || 0), 0);
  const overallProgress = totalTarget > 0 ? Math.min(100, (totalSaved / totalTarget) * 100) : 0;

  return (
    <div style={{ ...S.card, background: cardBg, borderColor: cardBorder, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: textColor }}>
          {'Savings Goals (' + goals.length + ')'}
        </div>
        <Link to="/savings" style={{ fontSize: 12, color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>View all</Link>
      </div>

      {/* Overall bar */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: textMuted, marginBottom: 4 }}>
          <span>{currency + Number(totalSaved).toLocaleString() + ' saved'}</span>
          <span style={{ fontWeight: 600, color: textColor }}>{overallProgress.toFixed(0) + '%'}</span>
        </div>
        <div style={{ width: '100%', background: inputBg, borderRadius: 20, height: 8 }}>
          <div style={{
            width: overallProgress + '%',
            background: 'linear-gradient(90deg, #3b82f6, #22c55e)',
            height: 8, borderRadius: 20, transition: 'width 0.5s',
          }} />
        </div>
      </div>

      {/* Goal rows (max 3) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {goals.slice(0, 3).map((goal) => {
          const color = goal.color || '#3b82f6';
          return (
            <div key={goal.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, background: color + '18',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0,
              }}>
                {goal.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                  <span style={{ fontWeight: 600, color: textColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{goal.name}</span>
                  <span style={{ color: textMuted, flexShrink: 0, marginLeft: 8 }}>{goal.progress.toFixed(0) + '%'}</span>
                </div>
                <div style={{ width: '100%', background: inputBg, borderRadius: 20, height: 6 }}>
                  <div style={{
                    width: goal.progress + '%',
                    background: `linear-gradient(90deg, ${color}, ${color}cc)`,
                    height: 6, borderRadius: 20, transition: 'width 0.5s',
                  }} />
                </div>
              </div>
            </div>
          );
        })}
        {goals.length > 3 && (
          <div style={{ fontSize: 11, color: textMuted, textAlign: 'center' }}>
            {'+' + (goals.length - 3) + ' more goals'}
          </div>
        )}
      </div>
    </div>
  );
}
